import type {
  ApiClient,
  ChangePasswordInput,
  CreateInstanceInput,
  CreateProjectInput,
  CreateProviderCredentialInput,
  CreateSecretInput,
  CreateServiceInput,
  CreateUserInput,
  DeployStackInput,
  EnrollToken,
  ExecStreamEndReason,
  ExecStreamHandle,
  ListAuditParams,
  ListContainersParams,
  ListDeploymentsParams,
  ListInstancesParams,
  ListNodesParams,
  ListProjectsParams,
  ListProviderCredentialsParams,
  ListSecretsParams,
  ListServicesParams,
  ListStacksParams,
  LoginInput,
  LogStreamEndReason,
  LogStreamHandle,
  OpenExecStreamParams,
  OpenLogStreamParams,
  Page,
  PageParams,
  RotateProviderCredentialInput,
  Session,
  UpdateProjectInput,
  UpdateServiceInput,
  UpdateUserInput,
} from "./client";
import type { AuditVerificationStatus } from "./types";
import { ApiError } from "./errors";
import { emitUnauthorized } from "./authEvents";
import { createExecStreamEmitter } from "./execStream";
import { createLogStreamEmitter } from "./logStream";
import type {
  Account,
  AuditEvent,
  AuditVerification,
  Container,
  ContainerEvent,
  Deployment,
  EntitlementStatus,
  Fleet,
  FleetNode,
  Instance,
  Project,
  ProviderCredentialSummary,
  SecretSummary,
  Service,
  Stack,
  StackMeta,
  Subscription,
  User,
} from "./types";

/**
 * HTTP implementation of `ApiClient` against the real control plane. Every
 * request is a RELATIVE path (`/api/...`, `/login`, `/logout`) with
 * `credentials: "include"` so the browser attaches/receives the httpOnly
 * session cookie automatically — this file never reads, stores, or sends a
 * bearer token (charter S-3/S-13). Relative paths also mean it works unmodified
 * behind both the vite dev proxy (`vite.config.ts` -> 127.0.0.1:8443) and the
 * nginx proxy in the built image (`nginx.conf`).
 */

type ContainerActionKind = "start" | "stop" | "restart";

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  path: string;
  body?: unknown;
  query?: Record<string, string | number | undefined>;
}

function buildUrl(path: string, query?: Record<string, string | number | undefined>): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === "") continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/** Most of the API surface has converged on the shared JSON error envelope
 * (`httpx.ErrorBody` — `{error, code, requestId}`, server/internal/httpx/
 * httpx.go), but a few older/lower-level paths (dockerproxy, the mesh
 * bridge, obs) still call the stdlib's `http.Error` and answer with plain
 * text. Try the JSON shape first and fall back to raw text so both keep
 * working — this is the one place that distinction is allowed to matter;
 * every caller of `rawRequest`/`request` just sees `ApiError.message`. */
async function safeErrorMessage(res: Response): Promise<string> {
  let text: string;
  try {
    text = (await res.text()).trim();
  } catch {
    return defaultMessageForStatus(res.status);
  }
  if (!text) return defaultMessageForStatus(res.status);

  try {
    const parsed = JSON.parse(text) as { error?: unknown };
    if (typeof parsed.error === "string" && parsed.error.trim()) {
      const message = parsed.error.trim();
      return message.charAt(0).toUpperCase() + message.slice(1);
    }
  } catch {
    // Not JSON — fall through to the plain-text path below.
  }
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function defaultMessageForStatus(status: number): string {
  switch (status) {
    case 400:
      return "That request wasn't valid.";
    case 401:
      return "Your session has expired. Sign in again.";
    case 402:
      return "That plan limit has been reached.";
    case 403:
      return "You don't have permission to do that.";
    case 404:
      return "Not found.";
    case 501:
      return "Not available yet.";
    case 502:
      return "The control plane couldn't reach that node.";
    case 503:
      return "The control plane is unavailable right now.";
    default:
      return "Something went wrong talking to the control plane.";
  }
}

function codeForStatus(status: number): string {
  switch (status) {
    case 401:
      return "unauthenticated";
    case 402:
      return "payment_required";
    case 403:
      return "forbidden";
    case 404:
      return "not_found";
    case 501:
      return "not_implemented";
    default:
      return `http_${status}`;
  }
}

// ---- CSRF double-submit (WEB-M2) ----
// The server mints a non-HttpOnly token cookie alongside the session cookie
// at login (server/internal/auth/http.go) specifically so this client can
// read it and echo it back — reading document.cookie is the intended use,
// not a workaround. Only a mutating request needs the header; GET never
// changes state, so it's never CSRF-relevant.
const CSRF_COOKIE_NAME = "comebye_csrf";
const CSRF_HEADER_NAME = "X-CSRF-Token";
const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

function readCsrfCookie(): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE_NAME}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

/** Performs the fetch, maps a non-2xx response to a thrown `ApiError`, and
 * returns the raw response body text (empty for a 204/205). Every other helper
 * in this file builds on this one so error handling and 401 detection stay in
 * exactly one place. */
async function rawRequest(options: RequestOptions): Promise<string> {
  const { method = "GET", path, body, query } = options;
  const url = buildUrl(path, query);

  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (MUTATING_METHODS.has(method)) {
    const csrfToken = readCsrfCookie();
    // No cookie means no session either (they're minted together) — omit
    // the header rather than send an empty one; the server's fail-closed
    // check treats "missing" and "blank" identically anyway.
    if (csrfToken) headers[CSRF_HEADER_NAME] = csrfToken;
  }

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: "include",
      headers: Object.keys(headers).length > 0 ? headers : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(
      "Couldn't reach the control plane. Check your connection and try again.",
      0,
      "network_error",
    );
  }

  if (!res.ok) {
    // A 401 on any authenticated call (never on /login itself, where it just
    // means "wrong credentials") means the session cookie is missing/expired
    // server-side. Tell AuthContext so it clears state and ProtectedRoute sends
    // the operator back to /login — see authEvents.ts.
    if (res.status === 401 && path !== "/login") {
      emitUnauthorized();
    }
    throw new ApiError(await safeErrorMessage(res), res.status, codeForStatus(res.status));
  }

  return res.text();
}

async function request<T>(options: RequestOptions): Promise<T> {
  const text = await rawRequest(options);
  return text ? (JSON.parse(text) as T) : (undefined as T);
}

const KNOWN_AUDIT_VERIFICATION_STATUSES: readonly AuditVerificationStatus[] = ["ok", "broken", "unverifiable"];

// parseAuditVerification narrows the raw JSON `/verify` response into an
// AuditVerification instead of trusting an unchecked `as AuditVerification` cast (GH-73
// audit round 5, MEDIUM-3). A version-skewed server, or a proxy/gateway that drops or
// corrupts fields, could hand back a `status` this client build doesn't recognize (or
// none at all) — an unchecked cast would let that value flow straight into
// VerifyResultBanner's switch, and any FUTURE branch written as an `else` (the exact bug
// this round's audit found there) would render it as a green "chain intact" banner.
// Fail closed here instead: an unrecognized/missing status is normalized to
// "unverifiable" — never silently trusted as "ok" — and `ok` is forced false unless the
// server both claims `ok: true` AND a recognized `status: "ok"` agree with each other.
function parseAuditVerification(raw: unknown): AuditVerification {
  const r = (raw && typeof raw === "object" ? raw : {}) as Partial<AuditVerification>;
  const status = KNOWN_AUDIT_VERIFICATION_STATUSES.includes(r.status as AuditVerificationStatus)
    ? (r.status as AuditVerificationStatus)
    : "unverifiable";
  return {
    ...r,
    status,
    ok: status === "ok" && r.ok === true,
    count: typeof r.count === "number" ? r.count : 0,
    fleetId: typeof r.fleetId === "string" ? r.fleetId : "",
  };
}

function fleetContainersPath(fleetId: string): string {
  return `/api/fleets/${encodeURIComponent(fleetId)}/containers`;
}

function containerPath(fleetId: string, nodeId: string, containerId: string): string {
  return `${fleetContainersPath(fleetId)}/${encodeURIComponent(nodeId)}/${encodeURIComponent(containerId)}`;
}

function containerLogsStreamUrl(fleetId: string, nodeId: string, containerId: string, tail?: number): string {
  return buildUrl(`${containerPath(fleetId, nodeId, containerId)}/logs/stream`, { tail });
}

// Endpoint path only — not yet backed by a server route (spike doc: "the
// browser WS bridge (C-8) doesn't exist server-side either"). Owned jointly
// with whoever builds the MeshRouter.OpenExec bridge; the path and the
// resize-as-text/stdout-as-binary frame split below are this client's
// proposal, not an agreed contract yet — expect this to change once that
// handshake happens.
function containerExecStreamUrl(fleetId: string, nodeId: string, containerId: string): string {
  return `${containerPath(fleetId, nodeId, containerId)}/exec/stream`;
}

// Exported so the reconnect-cap test can assert against the real value
// rather than a hardcoded copy of it.
export const MAX_LOG_STREAM_RECONNECT_ATTEMPTS = 5;

function isLogStreamEndReason(value: unknown): value is LogStreamEndReason {
  return value === "stopped" || value === "truncated" || value === "error";
}

function isExecStreamEndReason(value: unknown): value is ExecStreamEndReason {
  return value === "exited" || value === "error" || value === "closed";
}

function fleetSecretsPath(fleetId: string): string {
  return `/api/fleets/${encodeURIComponent(fleetId)}/secrets`;
}

function secretPath(fleetId: string, secretId: string): string {
  return `${fleetSecretsPath(fleetId)}/${encodeURIComponent(secretId)}`;
}

function fleetStacksPath(fleetId: string): string {
  return `/api/fleets/${encodeURIComponent(fleetId)}/stacks`;
}

function stackPath(fleetId: string, stackId: string): string {
  return `${fleetStacksPath(fleetId)}/${encodeURIComponent(stackId)}`;
}

// ---- Admin/user portal path helpers ----

function usersPath(): string {
  return "/api/users";
}

function userPath(id: string): string {
  return `${usersPath()}/${encodeURIComponent(id)}`;
}

function fleetAuditPath(fleetId: string): string {
  return `/api/fleets/${encodeURIComponent(fleetId)}/audit`;
}

// ---- Hero flow path helpers. Mirrors the real route table exactly
// (server/internal/httpapi/server.go's WithProjects/WithProvisioning
// mounts) ----

function fleetProjectsPath(fleetId: string): string {
  return `/api/fleets/${encodeURIComponent(fleetId)}/projects`;
}

function projectPath(fleetId: string, projectId: string): string {
  return `${fleetProjectsPath(fleetId)}/${encodeURIComponent(projectId)}`;
}

function projectServicesPath(fleetId: string, projectId: string): string {
  return `${projectPath(fleetId, projectId)}/services`;
}

function servicePath(fleetId: string, projectId: string, serviceId: string): string {
  return `${projectServicesPath(fleetId, projectId)}/${encodeURIComponent(serviceId)}`;
}

function fleetProviderCredentialsPath(fleetId: string): string {
  return `/api/fleets/${encodeURIComponent(fleetId)}/provider-credentials`;
}

function providerCredentialPath(fleetId: string, id: string): string {
  return `${fleetProviderCredentialsPath(fleetId)}/${encodeURIComponent(id)}`;
}

function fleetInstancesPath(fleetId: string): string {
  return `/api/fleets/${encodeURIComponent(fleetId)}/instances`;
}

function instancePath(fleetId: string, id: string): string {
  return `${fleetInstancesPath(fleetId)}/${encodeURIComponent(id)}`;
}

async function containerAction(
  fleetId: string,
  nodeId: string,
  containerId: string,
  action: ContainerActionKind,
): Promise<Container> {
  const text = await rawRequest({ method: "POST", path: `${containerPath(fleetId, nodeId, containerId)}/${action}` });
  if (text) return JSON.parse(text) as Container;
  // The server falls back to a bare 204 when the action itself succeeded but its
  // own post-action re-inspect couldn't complete (fleetapi.ContainerActionHandler).
  // Re-fetch so the caller still gets the Container the contract promises.
  return request<Container>({ path: containerPath(fleetId, nodeId, containerId) });
}

export function createRealApiClient(): ApiClient {
  return {
    async listFleets(): Promise<Fleet[]> {
      return request<Fleet[]>({ path: "/api/fleets" });
    },

    async login({ username, password }: LoginInput): Promise<Session> {
      return request<Session>({ method: "POST", path: "/login", body: { username, password } });
    },

    async logout(): Promise<void> {
      await request<void>({ method: "POST", path: "/logout" });
    },

    async listContainers(params: ListContainersParams): Promise<Page<Container>> {
      return request<Page<Container>>({
        path: fleetContainersPath(params.fleetId),
        query: { nodeId: params.nodeId, stackId: params.stackId, cursor: params.cursor, limit: params.limit },
      });
    },

    async getContainer(fleetId: string, nodeId: string, containerId: string): Promise<Container> {
      return request<Container>({ path: containerPath(fleetId, nodeId, containerId) });
    },

    async startContainer(fleetId: string, nodeId: string, containerId: string): Promise<Container> {
      return containerAction(fleetId, nodeId, containerId, "start");
    },

    async stopContainer(fleetId: string, nodeId: string, containerId: string): Promise<Container> {
      return containerAction(fleetId, nodeId, containerId, "stop");
    },

    async restartContainer(fleetId: string, nodeId: string, containerId: string): Promise<Container> {
      return containerAction(fleetId, nodeId, containerId, "restart");
    },

    async listNodes(params: ListNodesParams): Promise<Page<FleetNode>> {
      return request<Page<FleetNode>>({
        path: `/api/fleets/${encodeURIComponent(params.fleetId)}/nodes`,
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    // ---- Stacks (C-9) ----
    // server/internal/stacksvc mounts these under /api/fleets/{fleet}/stacks.
    // The list is metadata only (StackMeta, no `compose` — see types.ts); the
    // canonical compose file rides along on get/deploy/redeploy only.

    async listStacks(params: ListStacksParams): Promise<Page<StackMeta>> {
      return request<Page<StackMeta>>({
        path: fleetStacksPath(params.fleetId),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    async getStack(fleetId: string, id: string): Promise<Stack> {
      return request<Stack>({ path: stackPath(fleetId, id) });
    },

    async deployStack(fleetId: string, input: DeployStackInput): Promise<Stack> {
      return request<Stack>({ method: "POST", path: fleetStacksPath(fleetId), body: input });
    },

    async redeployStack(fleetId: string, id: string): Promise<Stack> {
      return request<Stack>({ method: "POST", path: `${stackPath(fleetId, id)}/redeploy` });
    },

    async deleteStack(fleetId: string, id: string): Promise<void> {
      await request<void>({ method: "DELETE", path: stackPath(fleetId, id) });
    },

    // ---- Secrets (C-2b): write-only, live server-side ----
    // server/internal/secretsvc mounts these under /api/fleets/{fleet}/secrets.
    // The wire shape (SecretMetaJSON) has no value field anywhere, on any of
    // these responses — see types.ts.

    async listSecrets(params: ListSecretsParams): Promise<Page<SecretSummary>> {
      return request<Page<SecretSummary>>({
        path: fleetSecretsPath(params.fleetId),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    async createSecret(fleetId: string, input: CreateSecretInput): Promise<SecretSummary> {
      return request<SecretSummary>({
        method: "POST",
        path: fleetSecretsPath(fleetId),
        body: input,
      });
    },

    async rotateSecret(fleetId: string, secretId: string, value: string): Promise<SecretSummary> {
      return request<SecretSummary>({
        method: "POST",
        path: `${secretPath(fleetId, secretId)}/rotate`,
        body: { value },
      });
    },

    async deleteSecret(fleetId: string, secretId: string): Promise<void> {
      await request<void>({
        method: "DELETE",
        path: secretPath(fleetId, secretId),
      });
    },

    // ---- Enrollment (C-6) ----

    async createEnrollToken(fleetId: string): Promise<EnrollToken> {
      // The server also returns id/agentId/label/tenant; the UI only needs the
      // token and its expiry, so those are the only fields lifted into the
      // client-facing shape (see EnrollToken in client.ts).
      return request<EnrollToken>({
        method: "POST",
        path: "/api/enroll/tokens",
        body: { tenant: fleetId },
      });
    },

    // ---- Live log tail (C-8a) ----

    openLogStream({ fleetId, nodeId, containerId, tail }: OpenLogStreamParams): LogStreamHandle {
      const url = containerLogsStreamUrl(fleetId, nodeId, containerId, tail);
      // `withCredentials` is belt-and-suspenders: this URL is always relative
      // (see the file header), so the browser already attaches the httpOnly
      // session cookie same-origin without it — matches `credentials:
      // "include"` on the fetch path above. Never a token in the URL (S-13).
      const source = new EventSource(url, { withCredentials: true });
      const emitter = createLogStreamEmitter(() => source.close());
      let failures = 0;

      source.addEventListener("log", (evt) => {
        const raw = (evt as MessageEvent<string>).data;
        try {
          const parsed = JSON.parse(raw) as { s?: unknown; m?: unknown };
          if (parsed.s === "stdout" || parsed.s === "stderr") {
            emitter.emitLine({ stream: parsed.s, message: typeof parsed.m === "string" ? parsed.m : "" });
          }
        } catch {
          // Malformed frame from the server — drop this one line rather than
          // take the whole panel down over it.
        }
      });

      source.addEventListener("end", (evt) => {
        const raw = (evt as MessageEvent<string>).data;
        let reason: LogStreamEndReason = "stopped";
        try {
          const parsed = JSON.parse(raw) as { reason?: unknown };
          if (isLogStreamEndReason(parsed.reason)) reason = parsed.reason;
        } catch {
          // Fall back to "stopped" — still a clean, expected close either way.
        }
        emitter.emitEnd(reason);
      });

      source.onopen = () => {
        failures = 0;
        emitter.emitStatus("open");
      };

      // EventSource retries on its own after any non-clean close (network
      // blip, server restart, proxy idle timeout) — desired, but silent
      // infinite retry is not, so this caps it and surfaces the in-between
      // state via `onStatus` rather than spinning quietly. Note: because a
      // reconnect re-tails from `tail=N`, an operator may briefly see a few
      // recent lines repeat after one — acceptable for this MVP.
      source.onerror = () => {
        if (source.readyState === EventSource.CLOSED) {
          // Either we closed it ourselves (close()/emitEnd already handled
          // teardown) or the browser gave up for good with no more retries.
          emitter.emitStatus("closed");
          return;
        }
        failures += 1;
        if (failures > MAX_LOG_STREAM_RECONNECT_ATTEMPTS) {
          source.close();
          emitter.emitStatus("closed");
          emitter.emitEnd("error");
          return;
        }
        emitter.emitStatus("reconnecting");
      };

      return emitter;
    },

    // ---- Interactive exec (SPIKE-3 / C-8) ----
    // Provisional wire framing pending agreement with whoever builds the
    // server-side WS<->Session bridge (see containerExecStreamUrl): stdin
    // and stdout ride binary WS frames verbatim (no envelope — proto/exec.go
    // already frames stdin/stdout on the tunnel side of the bridge, so this
    // client doesn't need to re-frame it), while resize and the terminal
    // end-of-session signal ride distinct text (JSON) frames so the binary
    // path stays a straight byte pass-through end to end (P-3 latency
    // budget: no base64, no JSON parsing on the keystroke round-trip).
    openExecStream({ fleetId, nodeId, containerId, cols, rows }: OpenExecStreamParams): ExecStreamHandle {
      const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const url = `${wsProtocol}//${window.location.host}${buildUrl(containerExecStreamUrl(fleetId, nodeId, containerId), { cols, rows })}`;
      const socket = new WebSocket(url);
      socket.binaryType = "arraybuffer";

      const emitter = createExecStreamEmitter(
        () => socket.close(),
        (data) => {
          if (socket.readyState === WebSocket.OPEN) socket.send(data);
        },
        (nextCols, nextRows) => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: "resize", cols: nextCols, rows: nextRows }));
          }
        },
      );

      socket.onopen = () => emitter.emitStatus("open");

      socket.onmessage = (evt) => {
        if (evt.data instanceof ArrayBuffer) {
          emitter.emitOutput(new Uint8Array(evt.data));
          return;
        }
        try {
          const parsed = JSON.parse(evt.data as string) as { type?: unknown; reason?: unknown };
          if (parsed.type === "end") {
            emitter.emitEnd(isExecStreamEndReason(parsed.reason) ? parsed.reason : "closed");
          }
        } catch {
          // Not a control frame this client understands — ignore rather
          // than tear down a session over one malformed message.
        }
      };

      socket.onclose = (evt) => {
        // A clean server-initiated close (code 1000) without a preceding
        // "end" control frame is treated the same as one — either way the
        // session is over and there's nothing left to reconnect (unlike
        // openLogStream, an exec session doesn't auto-resume after a drop;
        // the shell process on the other end is gone).
        emitter.emitStatus("closed");
        emitter.emitEnd(evt.code === 1000 ? "exited" : "error");
      };

      socket.onerror = () => {
        // The close event still follows an error, so emitEnd happens there
        // — this only needs to reflect the in-between state.
        emitter.emitStatus("reconnecting");
      };

      return emitter;
    },

    subscribeToContainerEvents(_fleetId: string, _onEvent: (event: ContainerEvent) => void): () => void {
      // P-1 seam, deliberately deferred: the real control plane has no SSE/WS
      // event stream yet, so this is a no-op that never calls onEvent. Container
      // state reaches the UI instead via the direct cache write on each
      // start/stop/restart mutation and a modest refetchInterval on the
      // containers list (both in queries.ts) — a stand-in, not a replacement for
      // the real thing. When the per-fleet event channel lands, implement a real
      // subscribe/unsubscribe here (open the channel, call onEvent per message,
      // close it on unsubscribe) and no caller anywhere else needs to change.
      return () => {};
    },

    // ---- Admin: users (admin/user portal) ----

    async listUsers(params: PageParams): Promise<Page<User>> {
      return request<Page<User>>({
        path: usersPath(),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    async createUser(input: CreateUserInput): Promise<User> {
      return request<User>({ method: "POST", path: usersPath(), body: input });
    },

    async updateUser(id: string, input: UpdateUserInput): Promise<User> {
      return request<User>({ method: "PATCH", path: userPath(id), body: input });
    },

    async resetUserPassword(id: string, password: string): Promise<void> {
      await request<void>({ method: "POST", path: `${userPath(id)}/password`, body: { password } });
    },

    async deleteUser(id: string): Promise<void> {
      await request<void>({ method: "DELETE", path: userPath(id) });
    },

    // ---- Account (the caller's own profile/credential) ----

    async getAccount(): Promise<Account> {
      return request<Account>({ path: "/api/account" });
    },

    async changePassword(input: ChangePasswordInput): Promise<void> {
      await request<void>({ method: "POST", path: "/api/account/password", body: input });
    },

    // ---- Audit log (C-13 — already live server-side) ----
    // server/internal/auditsvc mounts these under /api/fleets/{fleet}/audit.
    // Same cursor/limit pagination shape as every other list in this file.

    async listAuditEvents(params: ListAuditParams): Promise<Page<AuditEvent>> {
      return request<Page<AuditEvent>>({
        path: fleetAuditPath(params.fleetId),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    async verifyAuditChain(fleetId: string): Promise<AuditVerification> {
      const raw = await request<unknown>({ path: `${fleetAuditPath(fleetId)}/verify` });
      return parseAuditVerification(raw);
    },

    // ---- Hero flow: projects (#63 projectsvc — real, merged route table) ----

    async listProjects(params: ListProjectsParams): Promise<Page<Project>> {
      return request<Page<Project>>({
        path: fleetProjectsPath(params.fleetId),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    async getProject(fleetId: string, id: string): Promise<Project> {
      return request<Project>({ path: projectPath(fleetId, id) });
    },

    async createProject(fleetId: string, input: CreateProjectInput): Promise<Project> {
      return request<Project>({ method: "POST", path: fleetProjectsPath(fleetId), body: input });
    },

    async updateProject(fleetId: string, id: string, input: UpdateProjectInput): Promise<Project> {
      return request<Project>({ method: "PATCH", path: projectPath(fleetId, id), body: input });
    },

    async deleteProject(fleetId: string, id: string): Promise<void> {
      await request<void>({ method: "DELETE", path: projectPath(fleetId, id) });
    },

    // ---- Hero flow: services (create/update/deploy/deployment history) ----

    async listServices(params: ListServicesParams): Promise<Page<Service>> {
      return request<Page<Service>>({
        path: projectServicesPath(params.fleetId, params.projectId),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    async getService(fleetId: string, projectId: string, id: string): Promise<Service> {
      return request<Service>({ path: servicePath(fleetId, projectId, id) });
    },

    async createService(fleetId: string, projectId: string, input: CreateServiceInput): Promise<Service> {
      return request<Service>({
        method: "POST",
        path: projectServicesPath(fleetId, projectId),
        body: input,
      });
    },

    async updateService(
      fleetId: string,
      projectId: string,
      id: string,
      input: UpdateServiceInput,
    ): Promise<Service> {
      return request<Service>({
        method: "PATCH",
        path: servicePath(fleetId, projectId, id),
        body: input,
      });
    },

    async deleteService(fleetId: string, projectId: string, id: string): Promise<void> {
      await request<void>({ method: "DELETE", path: servicePath(fleetId, projectId, id) });
    },

    // NOT IMPLEMENTED server-side: this always rejects with a 501 ApiError
    // (see ApiClient.deployService's doc comment). Still calls the real
    // route, deliberately — an unauthorized/unknown-service caller gets the
    // 403/404 it would get once deploying is real, and no path in this
    // client fabricates a "deploying" state from an empty-object POST.
    async deployService(fleetId: string, projectId: string, id: string): Promise<never> {
      return request<never>({
        method: "POST",
        path: `${servicePath(fleetId, projectId, id)}/deploy`,
        body: {},
      });
    },

    async listDeployments(params: ListDeploymentsParams): Promise<Page<Deployment>> {
      return request<Page<Deployment>>({
        path: `${servicePath(params.fleetId, params.projectId, params.serviceId)}/deployments`,
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    // ---- Hero flow: provider credentials (write-only — S-13). Mounted only
    // when COMEBYE_PROVISIONING=1; unmounted, these fall through to the
    // catch-all and answer a plain-text 501 (see safeErrorMessage above) —
    // callers should treat that as "not enabled," not a bug. ----

    async listProviderCredentials(params: ListProviderCredentialsParams): Promise<Page<ProviderCredentialSummary>> {
      return request<Page<ProviderCredentialSummary>>({
        path: fleetProviderCredentialsPath(params.fleetId),
        query: { cursor: params.cursor, limit: params.limit },
      });
    },

    async createProviderCredential(
      fleetId: string,
      input: CreateProviderCredentialInput,
    ): Promise<ProviderCredentialSummary> {
      return request<ProviderCredentialSummary>({
        method: "POST",
        path: fleetProviderCredentialsPath(fleetId),
        body: input,
      });
    },

    async rotateProviderCredential(
      fleetId: string,
      id: string,
      input: RotateProviderCredentialInput,
    ): Promise<ProviderCredentialSummary> {
      return request<ProviderCredentialSummary>({
        method: "PUT",
        path: providerCredentialPath(fleetId, id),
        body: input,
      });
    },

    async deleteProviderCredential(fleetId: string, id: string): Promise<void> {
      await request<void>({ method: "DELETE", path: providerCredentialPath(fleetId, id) });
    },

    // ---- Hero flow: instances (Hetzner provisioning; same
    // COMEBYE_PROVISIONING gating as provider credentials above) ----

    async listInstances(params: ListInstancesParams): Promise<Page<Instance>> {
      return request<Page<Instance>>({
        path: fleetInstancesPath(params.fleetId),
        query: { cursor: params.cursor, limit: params.limit, projectId: params.projectId },
      });
    },

    async getInstance(fleetId: string, id: string): Promise<Instance> {
      return request<Instance>({ path: instancePath(fleetId, id) });
    },

    async createInstance(fleetId: string, input: CreateInstanceInput): Promise<Instance> {
      return request<Instance>({ method: "POST", path: fleetInstancesPath(fleetId), body: input });
    },

    async deleteInstance(fleetId: string, id: string): Promise<void> {
      await request<void>({ method: "DELETE", path: instancePath(fleetId, id) });
    },

    // ---- Billing (L-1) ----
    // No tenant/fleet segment on any of these paths, matching the server: the
    // tenant comes only from the session cookie (S-17), so nothing this client
    // sends can aim them at another tenant's billing.
    async getSubscription(): Promise<Subscription> {
      return request<Subscription>({ path: "/api/billing/subscription" });
    },

    async startCheckout(email?: string): Promise<string> {
      const { url } = await request<{ url: string }>({
        method: "POST",
        path: "/api/billing/checkout",
        body: email ? { email } : {},
      });
      return url;
    },

    async openBillingPortal(): Promise<string> {
      const { url } = await request<{ url: string }>({ method: "POST", path: "/api/billing/portal" });
      return url;
    },

    // ---- Licensing / entitlement (ADR-0017) ----
    // server/internal/entitlements mounts this at GET /api/entitlement, scoped
    // to the session's own tenant only (S-17) — same "no tenant parameter
    // anywhere" shape as the billing calls above.
    async getEntitlement(): Promise<EntitlementStatus> {
      return request<EntitlementStatus>({ path: "/api/entitlement" });
    },
  };
}
